import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Button } from '@/components/Button';
import { SearchableDropdown } from '@/components/SearchableDropdown';
import { SideBarModal } from '@/components/Modal';
import { StepIndicator } from '@/components/StepIndicator';
import MaintenanceDetailView from './MaintenanceDetailView';
import {
  Maintenance,
  MaintenanceReason,
  MaintenanceSeverity,
  MaintenanceCategory,
} from '@/types/common';
import { Vehicle } from '@/services/api/vehicles';
import {
  formatMitraLabel,
  getMitraID,
  getMitraName,
  useMitraOptions,
} from '@/hooks/useMitraOptions';
import { getListProvider, Provider } from '@/services/api/providers';
import { getListOem } from '@/services/api/oems';
import { vehiclesApi } from '@/services/api/vehicles';
import {
  extractApiErrorMessage,
  openImageFileDialog,
  getBucketUrlImageV2,
  uploadFileImage,
  getMaintenanceReasonLabel,
  getMaintenanceSeverityLabel,
  getMaintenanceCategoryLabel,
  getCostResponsibilityLabel,
  MAINTENANCE_COST_RESPONSIBILITIES,
} from '@/utils';
import { v4 as uuidv4 } from 'uuid';
import { showToast } from '@/components/toast/globalToast';
import { CreateMaintenanceRequest } from '@/services/api/maintenances';
import { getListHandover } from '@/services/api/handovers';
import { getListIncidents, Incident } from '@/services/api/incidents';

const VALIDATION_RULES = {
  DESCRIPTION_MAX_LENGTH: 500,
  NOTES_MAX_LENGTH: 1000,
  MIN_COST: 0,
  COST_STEP: 1000,
  ESTIMATED_COST_MAX: 1000000000000,
} as const;

const FORM_STEPS = {
  GENERAL_DATA: 1,
  PHOTO_NOTES: 2,
} as const;

const MAINTENANCE_REASONS = [
  MaintenanceReason.HUB_AUDIT,
  MaintenanceReason.SCHEDULED_MAINTENANCE,
  MaintenanceReason.LINKED_TO_INCIDENT,
  // MaintenanceReason.MITRA_REPORT,
  MaintenanceReason.POST_HANDOVER_INSPECTION,
];

const MAINTENANCE_SEVERITIES = [
  MaintenanceSeverity.LOW,
  MaintenanceSeverity.MEDIUM,
  MaintenanceSeverity.HIGH,
  MaintenanceSeverity.CRITICAL,
];

const MAINTENANCE_CATEGORIES = [
  MaintenanceCategory.BATTERY_REPLACEMENT,
  MaintenanceCategory.MOTOR_BLDC_REPAIR,
  MaintenanceCategory.TIRE_CHANGE,
  MaintenanceCategory.BRAKE_SYSTEM,
  MaintenanceCategory.BODY_REPAIR,
  MaintenanceCategory.ELECTRICAL_SYSTEM,
  MaintenanceCategory.FRAME_DAMAGE,
  MaintenanceCategory.GENERAL_MAINTENANCE,
  MaintenanceCategory.OTHER,
];

interface ManualFormData {
  reason: MaintenanceReason | '';
  incidentID: string;
  linkedIncidentCode?: string;
  vehicleID: string;
  driverID: number;
  driverCode: string;
  driverName: string;
  categories: MaintenanceCategory[];
  issueDescription: string;
  maintenanceDate: string;
  severity: MaintenanceSeverity | '';
  estimatedCost: number;
  costResponsibility: string;
  clientID: number;
  clientName: string;
  oemID: string;
  oemServiceCenter: string;
  expectedCompletionDate: string;
  vehicleImageURL?: string;
  notes: string;
}

interface ValidationErrors {
  [key: string]: string | undefined;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isEdit?: boolean;
  maintenance?: Maintenance | null;
  onSubmit: (data: CreateMaintenanceRequest) => void;
  isLoading?: boolean;
  clearFormOnClose?: boolean;
  initialFormData?: Partial<ManualFormData>;
  disableVehicleSelection?: boolean;
  initialVehicleLabel?: string;
  disableReason?: boolean;
  disableIncidentSelection?: boolean;
  disableCostResponsibility?: boolean;
  initialIncidentLabel?: string;
}

const createDefaultFormData = (): ManualFormData => ({
  reason: '',
  incidentID: '',
  vehicleID: '',
  driverID: 0,
  driverCode: '',
  driverName: '',
  categories: [],
  issueDescription: '',
  maintenanceDate: '',
  severity: '',
  estimatedCost: 0,
  costResponsibility: '',
  clientID: 0,
  clientName: '',
  oemID: '',
  oemServiceCenter: '',
  expectedCompletionDate: '',
  vehicleImageURL: '',
  notes: '',
});

const CreateMaintenanceModal: React.FC<Props> = ({
  isOpen,
  onClose,
  isEdit = false,
  maintenance,
  onSubmit,
  isLoading = false,
  clearFormOnClose = false,
  initialFormData,
  disableVehicleSelection = false,
  initialVehicleLabel,
  disableReason = false,
  disableIncidentSelection = false,
  disableCostResponsibility = false,
  initialIncidentLabel,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<ManualFormData>(
    createDefaultFormData
  );

  const [errors, setErrors] = useState<ValidationErrors>({});

  const [currentStep, setCurrentStep] = useState<number>(
    FORM_STEPS.GENERAL_DATA
  );
  const [detailTab, setDetailTab] = useState<'details' | 'notes'>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);

  // IDR display state for dot separator
  const [idrDisplayCost, setIdrDisplayCost] = useState('');

  const handleCostChange = (raw: string) => {
    const digitsOnly = raw.replace(/[^\d]/g, '');
    if (digitsOnly === '') {
      setIdrDisplayCost('');
      handleInputChange('estimatedCost', 0);
      return;
    }
    const numValue = parseInt(digitsOnly, 10);
    const formatted = numValue === 0 ? '0' : numValue.toLocaleString('id-ID');
    setIdrDisplayCost(formatted);
    handleInputChange('estimatedCost', numValue);
  };

  const [imagePreview, setImagePreview] = useState('');
  const [tempThumbnail, setTempThumbnail] = useState<{
    name: string;
    base64: string;
    size?: number;
  } | null>(null);

  const [providers, setProviders] = useState<Provider[]>([]);
  const [oems, setOems] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loadingIncidents, setLoadingIncidents] = useState(false);

  // Track whether fields were pre-filled from an incident selection
  const [incidentPrefilled, setIncidentPrefilled] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingOems, setLoadingOems] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  // Mitra is only picked when the cost lands on them, and never while the
  // field is pre-filled from an incident — so the list is only fetched then.
  const isMitraSelectionDisabled =
    incidentPrefilled ||
    disableCostResponsibility ||
    formData.reason === MaintenanceReason.LINKED_TO_INCIDENT;
  const {
    options: mitraOptions,
    loading: loadingMitras,
    hasMore: hasMoreMitras,
    setSearchTerm: setMitraSearchTerm,
    loadMore: loadMoreMitras,
    getDriver: getMitra,
  } = useMitraOptions({
    enabled:
      isOpen &&
      formData.costResponsibility === 'MITRA' &&
      !isMitraSelectionDisabled,
  });
  const selectedMitraLabel =
    formData.driverID > 0
      ? formatMitraLabel(formData.driverCode, formData.driverName)
      : undefined;

  const fetchClientData = useCallback(async () => {
    try {
      setLoadingProviders(true);
      const response = await getListProvider({
        page: '1',
        size: '10000',
      });
      setProviders(response.data || []);
    } catch (error) {
      showToast('error', 'Error', 'Failed to load providers');
      setProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  }, []);

  const fetchOemData = useCallback(async () => {
    try {
      setLoadingOems(true);
      const response = await getListOem({
        page: '1',
        size: '10000',
        partnershipStatuses: 'ACTIVE',
      });
      setOems(response.data || []);
    } catch (error) {
      showToast('error', 'Error', 'Failed to load OEMs');
      setOems([]);
    } finally {
      setLoadingOems(false);
    }
  }, []);

  const fetchIncidents = useCallback(async () => {
    try {
      setLoadingIncidents(true);
      const response = await getListIncidents({
        page: '1',
        size: '10000',
        statuses: 'OPEN',
      });
      setIncidents(response.data || []);
    } catch (error) {
      showToast('error', 'Error', 'Failed to load incidents');
      setIncidents([]);
    } finally {
      setLoadingIncidents(false);
    }
  }, []);

  const fetchVehicles = useCallback(async () => {
    try {
      setLoadingVehicles(true);
      const response = await vehiclesApi.getVehicles(1, 1000, '', {
        statuses: 'IDLE,BROKEN',
        isAvailable: true,
      });
      setVehicles(response.data || []);
    } catch (error) {
      showToast('error', 'Error', 'Failed to load vehicles');
      setVehicles([]);
    } finally {
      setLoadingVehicles(false);
    }
  }, []);

  const handleInputChange = useCallback(
    (
      field: keyof ManualFormData,
      value:
        | string
        | string[]
        | number
        | MaintenanceReason
        | MaintenanceSeverity
        | MaintenanceCategory[]
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors]
  );

  useEffect(() => {
    if (formData.reason === MaintenanceReason.LINKED_TO_INCIDENT) {
      fetchIncidents();
      return;
    }

    // Clear incident-related fields when reason changes away from LINKED_TO_INCIDENT
    if (formData.incidentID) {
      handleInputChange('incidentID', '');
      setIncidentPrefilled(false);
    }
  }, [formData.reason, formData.incidentID, fetchIncidents, handleInputChange]);

  const fetchDriverForVehicle = useCallback(
    async (vehicleID: string) => {
      try {
        const response = await getListHandover({
          statuses: 'ACTIVE',
          page: '1',
          size: '1000',
        });

        if (response.data && response.data.length > 0) {
          const vehicleHandover = response.data.find(
            (handover) => handover.vehicleID === vehicleID
          );

          if (vehicleHandover) {
            handleInputChange(
              'driverID',
              vehicleHandover.driverID
                ? parseInt(vehicleHandover.driverID.toString()) || 0
                : 0
            );
            handleInputChange('driverCode', vehicleHandover.driverCode || '');
            handleInputChange('driverName', vehicleHandover.driverName || '');
          } else {
            handleInputChange('driverID', 0);
            handleInputChange('driverCode', '');
            handleInputChange('driverName', '');
          }
        } else {
          handleInputChange('driverID', 0);
          handleInputChange('driverCode', '');
          handleInputChange('driverName', '');
        }
      } catch (error) {
        handleInputChange('driverID', 0);
        handleInputChange('driverCode', '');
        handleInputChange('driverName', '');
      }
    },
    [handleInputChange]
  );

  const handleMitraSelection = useCallback(
    (mitraId: string) => {
      if (mitraId) {
        const selectedMitra = getMitra(mitraId);

        if (selectedMitra) {
          handleInputChange('driverID', Number(getMitraID(selectedMitra)) || 0);
          handleInputChange('driverCode', selectedMitra.code || '');
          handleInputChange('driverName', getMitraName(selectedMitra));
        }
      } else {
        handleInputChange('driverID', 0);
        handleInputChange('driverCode', '');
        handleInputChange('driverName', '');
      }
    },
    [getMitra, handleInputChange]
  );

  const handleClientSelection = useCallback(
    (clientId: string) => {
      const numericClientId = clientId ? parseInt(clientId) || 0 : 0;
      handleInputChange('clientID', numericClientId);

      if (clientId) {
        const selectedClient = providers.find(
          (provider) =>
            (provider.id?.toString() || provider.ID?.toString()) === clientId
        );

        if (selectedClient) {
          handleInputChange('clientName', selectedClient.name || '');
        }
      } else {
        handleInputChange('clientName', '');
      }
    },
    [providers, handleInputChange]
  );

  const handleOemSelection = useCallback(
    (oemId: string) => {
      handleInputChange('oemID', oemId || '');
    },
    [handleInputChange]
  );

  const handleIncidentSelection = useCallback(
    (incidentId: string) => {
      handleInputChange('incidentID', incidentId);

      if (incidentId) {
        const selectedIncident = incidents.find((inc) => inc.ID === incidentId);

        if (selectedIncident) {
          // Pre-fill plate number (vehicleID)
          if (selectedIncident.vehicleID) {
            handleInputChange('vehicleID', selectedIncident.vehicleID);
            fetchDriverForVehicle(selectedIncident.vehicleID);
          }

          // Pre-fill cost responsibility from incident
          if (selectedIncident.costResponsibility) {
            handleInputChange(
              'costResponsibility',
              selectedIncident.costResponsibility
            );
            if (selectedIncident.costResponsibility === 'MITRA') {
              handleMitraSelection(
                selectedIncident.driverCode?.toString() || ''
              );
            }
            if (selectedIncident.costResponsibility === 'CLIENT') {
              handleClientSelection(
                selectedIncident.clientID?.toString() || ''
              );
            }
            if (selectedIncident.costResponsibility === 'OEM') {
              handleOemSelection(selectedIncident.oemID?.toString() || '');
            }
          }

          setIncidentPrefilled(true);
        }
      } else {
        // Cleared incident — reset pre-filled fields
        setIncidentPrefilled(false);
        handleInputChange('vehicleID', '');
        handleInputChange('driverID', 0);
        handleInputChange('driverCode', '');
        handleInputChange('driverName', '');
        handleInputChange('costResponsibility', '');
      }
    },
    [
      incidents,
      handleInputChange,
      fetchDriverForVehicle,
      handleMitraSelection,
      handleClientSelection,
      handleOemSelection,
    ]
  );

  const validateReason = useCallback(
    (reason: MaintenanceReason | ''): string | undefined => {
      if (!reason) {
        return 'Reason is required';
      }
      if (!MAINTENANCE_REASONS.includes(reason as MaintenanceReason)) {
        return 'Invalid reason selected';
      }
      return undefined;
    },
    []
  );

  const validateIncidentID = useCallback(
    (
      reason: MaintenanceReason | '',
      incidentID: string
    ): string | undefined => {
      if (reason !== MaintenanceReason.LINKED_TO_INCIDENT) return undefined;
      if (!incidentID) return 'Incident ID is required';
      return undefined;
    },
    []
  );

  const validateMaintenanceDetails = useCallback(
    (categories: MaintenanceCategory[]): string | undefined => {
      if (!categories || categories.length === 0) {
        return 'At least one maintenance detail is required';
      }
      const invalidCategories = categories.filter(
        (cat) => !MAINTENANCE_CATEGORIES.includes(cat)
      );
      if (invalidCategories.length > 0) {
        return `Invalid maintenance categories: ${invalidCategories.join(
          ', '
        )}`;
      }
      return undefined;
    },
    []
  );

  const validateSeverity = useCallback(
    (severity: MaintenanceSeverity | ''): string | undefined => {
      if (!severity) {
        return 'Severity is required';
      }
      if (!MAINTENANCE_SEVERITIES.includes(severity as MaintenanceSeverity)) {
        return 'Invalid severity selected';
      }
      return undefined;
    },
    []
  );

  const validateEstimatedCost = useCallback(
    (cost: number): string | undefined => {
      if (cost === undefined || cost === null || isNaN(cost)) {
        return 'Estimated cost is required';
      }

      if (cost < 0) {
        return 'Estimated cost must be at least 0';
      }

      if (cost > VALIDATION_RULES.ESTIMATED_COST_MAX) {
        return `Estimated cost cannot exceed ${VALIDATION_RULES.ESTIMATED_COST_MAX.toLocaleString()}`;
      }
      return undefined;
    },
    []
  );

  const isStep1Valid = useCallback((): boolean => {
    const reasonError = validateReason(formData.reason);
    const issueError = validateIncidentID(formData.reason, formData.incidentID);
    const vehicleError = formData.vehicleID ? undefined : 'Vehicle is required';
    const categoriesError = validateMaintenanceDetails(formData.categories);
    const descriptionError = formData.issueDescription
      ? undefined
      : 'Description is required';
    const dateError = formData.maintenanceDate
      ? undefined
      : 'Maintenance date is required';
    const severityError = validateSeverity(formData.severity);
    const costError = validateEstimatedCost(formData.estimatedCost);
    const responsibilityError = formData.costResponsibility
      ? undefined
      : 'Cost responsibility is required';

    let specificSelectionError: string | undefined = undefined;
    if (formData.costResponsibility === 'MITRA' && !formData.driverID) {
      specificSelectionError = 'Mitra selection is required';
    } else if (formData.costResponsibility === 'CLIENT' && !formData.clientID) {
      specificSelectionError = 'Client selection is required';
    } else if (formData.costResponsibility === 'OEM' && !formData.oemID) {
      specificSelectionError = 'OEM selection is required';
    }

    const oemError = formData.oemServiceCenter
      ? undefined
      : 'OEM service center is required';
    const completionDateError = formData.expectedCompletionDate
      ? undefined
      : 'Expected completion date is required';

    return (
      !reasonError &&
      !issueError &&
      !vehicleError &&
      !categoriesError &&
      !descriptionError &&
      !dateError &&
      !severityError &&
      !costError &&
      !responsibilityError &&
      !specificSelectionError &&
      !oemError &&
      !completionDateError
    );
  }, [
    formData,
    validateReason,
    validateIncidentID,
    validateMaintenanceDetails,
    validateSeverity,
    validateEstimatedCost,
  ]);

  const handleStepNavigation = useCallback(
    (step: number) => {
      if (!isEdit && step === FORM_STEPS.PHOTO_NOTES && !isStep1Valid()) {
        // Set validation errors when trying to proceed without valid data
        const newErrors: ValidationErrors = {};

        const reasonError = validateReason(formData.reason);
        if (reasonError) newErrors.reason = reasonError;

        const issueError = validateIncidentID(
          formData.reason,
          formData.incidentID
        );
        if (issueError) newErrors.incidentID = issueError;

        if (!formData.vehicleID) newErrors.vehicleID = 'Vehicle is required';

        const categoriesError = validateMaintenanceDetails(formData.categories);
        if (categoriesError) newErrors.categories = categoriesError;

        if (!formData.issueDescription)
          newErrors.issueDescription = 'Description is required';

        if (!formData.maintenanceDate)
          newErrors.maintenanceDate = 'Maintenance date is required';

        const severityError = validateSeverity(formData.severity);
        if (severityError) newErrors.severity = severityError;

        const costError = validateEstimatedCost(formData.estimatedCost);
        if (costError) newErrors.estimatedCost = costError;

        if (!formData.costResponsibility)
          newErrors.costResponsibility = 'Cost responsibility is required';

        if (formData.costResponsibility === 'MITRA' && !formData.driverID) {
          newErrors.driverID = 'Mitra selection is required';
        } else if (
          formData.costResponsibility === 'CLIENT' &&
          !formData.clientID
        ) {
          newErrors.clientID = 'Client selection is required';
        } else if (formData.costResponsibility === 'OEM' && !formData.oemID) {
          newErrors.oemID = 'OEM selection is required';
        }

        if (!formData.oemServiceCenter)
          newErrors.oemServiceCenter = 'OEM service center is required';

        if (!formData.expectedCompletionDate)
          newErrors.expectedCompletionDate =
            'Expected completion date is required';

        setErrors(newErrors);
        return;
      }

      setCurrentStep(step);
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = 0;
        }
      }, 0);
    },
    [
      isEdit,
      isStep1Valid,
      formData,
      validateReason,
      validateIncidentID,
      validateMaintenanceDetails,
      validateSeverity,
      validateEstimatedCost,
    ]
  );

  const handleImageUpload = useCallback(async () => {
    openImageFileDialog(
      async (file: { name: string; base64: string; size: number } | null) => {
        try {
          setIsSubmitting(true);

          if (!file) {
            setIsSubmitting(false);
            return;
          }

          const uuid = uuidv4();

          const response = await getBucketUrlImageV2({
            type: 'maintenance',
            uuid: uuid,
          });

          if (!response?.data) {
            throw new Error('Failed to get upload URL');
          }

          const url = response.data;
          const base64Data = file?.base64?.split(',')[1];

          if (!base64Data) {
            throw new Error('Invalid file data');
          }

          if (file?.base64) {
            setImagePreview(file.base64);
          }

          const upload = await uploadFileImage(url, base64Data, file?.name);

          if (upload.status === 200) {
            setTempThumbnail(file);
            handleInputChange('vehicleImageURL', url);
            showToast('success', 'Success', 'Upload image success');
          } else {
            throw new Error('Upload failed');
          }
        } catch (error: any) {
          const errorMessage = extractApiErrorMessage(
            error,
            'Failed to upload image'
          );
          showToast('error', 'Error', errorMessage);

          setTempThumbnail(null);
          setImagePreview('');
        } finally {
          setIsSubmitting(false);
        }
      }
    );
  }, [handleInputChange]);

  const handleRemoveImage = useCallback(() => {
    setImagePreview('');
    setTempThumbnail(null);
    handleInputChange('vehicleImageURL', '');
  }, [handleInputChange]);

  const onFormSubmit = useCallback(
    async (data: ManualFormData) => {
      try {
        setIsSubmitting(true);

        const submitData: CreateMaintenanceRequest = {
          reason: data.reason as MaintenanceReason,
          vehicleID: data.vehicleID,
          incidentID: data.incidentID || undefined,
          categories: data.categories,
          issueDescription: data.issueDescription,
          maintenanceDate: data.maintenanceDate,
          severity: data.severity as MaintenanceSeverity,
          estimatedCost: data.estimatedCost,
          costResponsibility: data.costResponsibility,
          oemServiceCenter: data.oemServiceCenter,
          expectedCompletionDate: data.expectedCompletionDate,
          notes: data.notes,
        };

        if (data.vehicleImageURL) {
          submitData.vehicleImageURL = data.vehicleImageURL;
        }

        if (data.costResponsibility === 'MITRA') {
          submitData.driverID = data.driverID;
          submitData.driverCode = data.driverCode;
          submitData.driverName = data.driverName;
        } else if (data.costResponsibility === 'OEM') {
          submitData.oemID = data.oemID;
        } else if (data.costResponsibility === 'CLIENT') {
          submitData.clientID = data.clientID;
          submitData.clientName = data.clientName;
        }

        await onSubmit(submitData);

        onClose();
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to submit maintenance form'
        );
        showToast('error', 'Error', errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, onClose]
  );

  const clearFormState = useCallback((prefill?: Partial<ManualFormData>) => {
    const nextFormData: ManualFormData = {
      ...createDefaultFormData(),
      ...prefill,
    };

    setFormData(nextFormData);
    setErrors({});
    setImagePreview(nextFormData.vehicleImageURL || '');
    setTempThumbnail(
      nextFormData.vehicleImageURL
        ? {
            name: 'existing_image.jpg',
            base64: nextFormData.vehicleImageURL,
          }
        : null
    );
    setCurrentStep(FORM_STEPS.GENERAL_DATA);
    setDetailTab('details');
    setIdrDisplayCost(
      nextFormData.estimatedCost > 0
        ? nextFormData.estimatedCost.toLocaleString('id-ID')
        : ''
    );
    setIncidentPrefilled(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const fetchAllData = async () => {
        try {
          await Promise.all([
            fetchClientData(),
            fetchOemData(),
            fetchVehicles(),
          ]);
        } catch (error) {}
      };

      fetchAllData();
    } else if (clearFormOnClose) {
      clearFormState();
    }
  }, [
    isOpen,
    fetchClientData,
    fetchOemData,
    fetchVehicles,
    clearFormOnClose,
    clearFormState,
  ]);

  useEffect(() => {
    if (isOpen) {
      if (maintenance) {
        setShowDetailView(isEdit);

        const resetData: ManualFormData = {
          reason: (maintenance.reason || '') as MaintenanceReason | '',
          incidentID: maintenance.incidentID || '',
          linkedIncidentCode:
            (maintenance as any).linkedIncidentCode ||
            maintenance.incidentID ||
            '',
          vehicleID: maintenance.vehicleID || '',
          driverID: maintenance.driverID
            ? parseInt(maintenance.driverID.toString()) || 0
            : 0,
          driverCode: maintenance.driverCode || '',
          driverName: maintenance.driverName || '',
          categories: (maintenance.categories || []) as MaintenanceCategory[],
          issueDescription: maintenance.issueDescription || '',
          maintenanceDate: maintenance.maintenanceDate || '',
          severity: (maintenance.severity || '') as MaintenanceSeverity | '',
          estimatedCost: maintenance.estimatedCost || 0,
          costResponsibility: maintenance.costResponsibility || '',
          clientID: maintenance.clientID || 0,
          clientName: maintenance.clientName || '',
          oemID: maintenance.oemID || '',
          oemServiceCenter: maintenance.oemServiceCenter || '',
          expectedCompletionDate: maintenance.expectedCompletionDate || '',
          vehicleImageURL: maintenance.vehicleImageURL || '',
          notes: maintenance.notes || '',
        };
        setFormData(resetData);
        // Sync the IDR display state so the estimated cost field shows the prefilled value
        const cost = maintenance.estimatedCost ?? 0;
        setIdrDisplayCost(cost === 0 ? '0' : cost.toLocaleString('id-ID'));
        setImagePreview(maintenance.vehicleImageURL || '');
        if (maintenance.vehicleImageURL) {
          setTempThumbnail({
            name: 'existing_image.jpg',
            base64: maintenance.vehicleImageURL,
          });
        }
      } else {
        setShowDetailView(false);
        clearFormState(initialFormData);
      }
    }
  }, [isOpen, maintenance, clearFormState, isEdit, initialFormData]);

  const steps = [
    { id: FORM_STEPS.GENERAL_DATA, label: 'General Data' },
    { id: FORM_STEPS.PHOTO_NOTES, label: 'Details' },
  ];

  const vehicleOptions = useMemo(() => {
    const options = vehicles.map((vehicle) => ({
      value: (vehicle.ID || vehicle.id)?.toString() || '',
      label: `${vehicle.platNomor || 'Unknown Plate'} - ${
        vehicle.oemName || 'Unknown OEM'
      } - ${vehicle.modelName || 'Unknown Model'}`,
    }));

    if (isEdit && maintenance) {
      const vehicleExists = options.some(
        (option) => option.value === maintenance.vehicleID
      );

      if (!vehicleExists) {
        return [
          {
            value: maintenance.vehicleID,
            label: `${maintenance.vehiclePlatNomor || 'Unknown Plate'} - ${
              maintenance.vehicleOemName || 'Unknown OEM'
            } - ${maintenance.vehicleModelName || 'Unknown Model'}`,
          },
          ...options,
        ];
      }
    }

    if (!isEdit && initialFormData?.vehicleID) {
      const vehicleExists = options.some(
        (option) => option.value === initialFormData.vehicleID
      );

      if (!vehicleExists) {
        return [
          {
            value: initialFormData.vehicleID,
            label: initialVehicleLabel || 'Selected vehicle',
          },
          ...options,
        ];
      }
    }

    // Include the incident's vehicle when it's not already in the list
    if (incidentPrefilled && formData.vehicleID) {
      const vehicleExists = options.some(
        (option) => option.value === formData.vehicleID
      );

      if (!vehicleExists) {
        const selectedIncident = incidents.find(
          (inc) => inc.ID === formData.incidentID
        );
        return [
          {
            value: formData.vehicleID,
            label: selectedIncident
              ? `${selectedIncident.platNomor || 'Unknown Plate'} - ${selectedIncident.oemName || 'Unknown OEM'} - ${selectedIncident.modelName || 'Unknown Model'}`
              : 'Selected Vehicle',
          },
          ...options,
        ];
      }
    }

    return options;
  }, [
    vehicles,
    isEdit,
    maintenance,
    initialFormData,
    initialVehicleLabel,
    incidentPrefilled,
    formData.vehicleID,
    formData.incidentID,
    incidents,
  ]);

  const incidentOptions = useMemo(() => {
    const options = incidents.map((incident) => ({
      value: incident.ID || '',
      label: incident.code
        ? `${incident.code} - ${incident.platNomor || ''}`
        : incident.ID || '',
    }));

    if (isEdit && maintenance) {
      const selectedIncidentId =
        formData.incidentID || maintenance.incidentID || '';
      const selectedIncidentValue =
        formData.incidentID ||
        formData.linkedIncidentCode ||
        selectedIncidentId;
      const selectedIncident = incidents.find(
        (incident) => incident.ID === selectedIncidentId
      );

      const incidentExists = options.some(
        (option) => option.value === selectedIncidentValue
      );

      if (selectedIncidentValue && !incidentExists) {
        return [
          {
            value: selectedIncidentValue,
            label: selectedIncident
              ? `${selectedIncident.code || selectedIncident.ID} - ${selectedIncident.platNomor || ''}`
              : formData.linkedIncidentCode || selectedIncidentId,
          },
          ...options,
        ];
      }
    }

    if (!isEdit && initialFormData?.incidentID) {
      const incidentExists = options.some(
        (option) => option.value === initialFormData.incidentID
      );

      if (!incidentExists) {
        return [
          {
            value: initialFormData.incidentID,
            label: initialIncidentLabel || initialFormData.incidentID,
          },
          ...options,
        ];
      }
    }

    return options;
  }, [
    incidents,
    isEdit,
    maintenance,
    formData.incidentID,
    formData.linkedIncidentCode,
    initialFormData,
    initialIncidentLabel,
  ]);

  const modalHeader = (
    <div>
      <h2 className="text-xl font-semibold text-gray-900">
        {isEdit ? 'Edit Maintenance' : 'Create Maintenance'}
        <p className="text-sm font-normal text-gray-600">
          {isEdit ? 'Update maintenance details.' : 'New Maintenance details'}
        </p>
      </h2>
      {isEdit ? (
        <></>
      ) : (
        <div className="flex justify-center mt-4">
          <StepIndicator steps={steps} currentStep={currentStep} />
        </div>
      )}
    </div>
  );

  if (isEdit && maintenance && showDetailView) {
    return (
      <SideBarModal
        isOpen={isOpen}
        onClose={onClose}
        header={modalHeader}
        width="sm"
        position="right"
        contentRef={scrollRef}
      >
        <div>
          <MaintenanceDetailView
            detailTab={detailTab}
            initialData={maintenance}
            onTabChange={(tab) => {
              setDetailTab(tab);
              setTimeout(() => {
                if (scrollRef.current) {
                  scrollRef.current.scrollTop = 0;
                }
              }, 0);
            }}
          />
          <div className="flex p-6 w-full mt-6">
            <Button
              onClick={() => {
                setShowDetailView(false);
                setCurrentStep(FORM_STEPS.GENERAL_DATA);
                setTimeout(() => {
                  if (scrollRef.current) {
                    scrollRef.current.scrollTop = 0;
                  }
                }, 0);
              }}
              variant="primary"
              className="w-full py-3"
            >
              Edit
            </Button>
          </div>
        </div>
      </SideBarModal>
    );
  }

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={onClose}
      header={modalHeader}
      width="sm"
      position="right"
      contentRef={scrollRef}
    >
      <div className="space-y-6">
        {currentStep === FORM_STEPS.GENERAL_DATA && (
          <div className="">
            <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 rounded-t-lg heading-field">
              Detail
            </div>
            {/* Reason */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                placeholder="e.g. Hub Audit"
                options={MAINTENANCE_REASONS.map((reason) => ({
                  value: reason,
                  label: getMaintenanceReasonLabel(reason),
                }))}
                value={formData.reason}
                disabled={
                  disableReason ||
                  formData.reason === MaintenanceReason.LINKED_TO_INCIDENT
                }
                onChange={(value) =>
                  handleInputChange('reason', value as MaintenanceReason)
                }
              />
              {errors.reason && (
                <p className="text-red-500 text-sm mt-1">{errors.reason}</p>
              )}
            </div>

            {formData.reason === MaintenanceReason.LINKED_TO_INCIDENT && (
              <div className="px-6 py-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Incident ID <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  placeholder={
                    loadingIncidents
                      ? 'Loading incidents...'
                      : 'Select incident'
                  }
                  options={incidentOptions}
                  value={formData.incidentID || formData.linkedIncidentCode}
                  onChange={handleIncidentSelection}
                  disabled={
                    loadingIncidents || isEdit || disableIncidentSelection
                  }
                />
                {errors.incidentID && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.incidentID}
                  </p>
                )}
              </div>
            )}

            {/* Plate Number */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plate Number <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                placeholder={
                  loadingVehicles ? 'Loading vehicles...' : 'e.g. B 1234 ABC'
                }
                options={vehicleOptions}
                value={formData.vehicleID}
                disabled={
                  loadingVehicles ||
                  isEdit ||
                  incidentPrefilled ||
                  disableVehicleSelection
                }
                onChange={(value) => {
                  handleInputChange('vehicleID', value);

                  if (value) {
                    fetchDriverForVehicle(value);
                  } else {
                    handleInputChange('driverID', 0);
                    handleInputChange('driverCode', '');
                    handleInputChange('driverName', '');
                  }
                }}
              />
              {errors.vehicleID && (
                <p className="text-red-500 text-sm mt-1">{errors.vehicleID}</p>
              )}
              {loadingVehicles && (
                <div className="flex items-center mt-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  <span className="text-sm text-gray-500">
                    Loading vehicles...
                  </span>
                </div>
              )}
            </div>

            {/* Maintenance Details */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maintenance Details <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                placeholder="e.g. BLDC Repair"
                options={MAINTENANCE_CATEGORIES.map((category) => ({
                  value: category,
                  label: getMaintenanceCategoryLabel(category),
                }))}
                value={formData.categories}
                onChange={(value) => {
                  const categories = Array.isArray(value)
                    ? (value as MaintenanceCategory[])
                    : [value as MaintenanceCategory];
                  handleInputChange('categories', categories);
                }}
                multiple
              />
              {errors.categories && (
                <p className="text-red-500 text-sm mt-1">{errors.categories}</p>
              )}
            </div>

            {/* Description */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <textarea
                  placeholder="e.g. BLDC rusak karena rider menembus banjir."
                  className="text-sm w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={4}
                  maxLength={VALIDATION_RULES.DESCRIPTION_MAX_LENGTH}
                  value={formData.issueDescription}
                  onChange={(e) =>
                    handleInputChange('issueDescription', e.target.value)
                  }
                />
                <div className="absolute bottom-2 right-3 text-xs text-gray-400">
                  {formData.issueDescription.length}/
                  {VALIDATION_RULES.DESCRIPTION_MAX_LENGTH}
                </div>
              </div>
              {errors.issueDescription && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.issueDescription}
                </p>
              )}
            </div>

            {/* Maintenance Date */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tanggal Maintenance <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.maintenanceDate}
                onChange={(e) =>
                  handleInputChange('maintenanceDate', e.target.value)
                }
              />
              {errors.maintenanceDate && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.maintenanceDate}
                </p>
              )}
            </div>

            {/* Severity */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Severity <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                placeholder="e.g. Medium"
                options={MAINTENANCE_SEVERITIES.map((severity) => ({
                  value: severity,
                  label: getMaintenanceSeverityLabel(severity),
                }))}
                value={formData.severity}
                onChange={(value) =>
                  handleInputChange('severity', value as MaintenanceSeverity)
                }
              />
              {errors.severity && (
                <p className="text-red-500 text-sm mt-1">{errors.severity}</p>
              )}
            </div>

            {/* Estimated Cost */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Cost <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm">Rp</span>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="0"
                  className="w-full border border-gray-300 rounded-md pl-10 pr-24 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                  value={idrDisplayCost}
                  onChange={(e) => handleCostChange(e.target.value)}
                />
                <div className="absolute inset-y-0 right-0 pr-3 pl-3 flex items-center pointer-events-none border-l border-gray-300">
                  <img
                    src="https://flagcdn.com/w40/id.png"
                    alt="IDR"
                    className="h-5 w-5 rounded-full object-cover mr-2 border border-gray-200"
                  />
                  <span className="text-gray-500 text-sm">IDR</span>
                </div>
              </div>
              {errors.estimatedCost && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.estimatedCost}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Isi 0 jika tidak ada biaya.
              </p>
            </div>

            {/* Biaya Ditanggung */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Biaya Ditanggung <span className="text-red-500">*</span>
              </label>
              <SearchableDropdown
                placeholder="e.g. Mitra"
                options={MAINTENANCE_COST_RESPONSIBILITIES.map(
                  (responsibility) => ({
                    value: responsibility,
                    label: getCostResponsibilityLabel(responsibility),
                  })
                )}
                value={formData.costResponsibility}
                disabled={
                  incidentPrefilled ||
                  disableCostResponsibility ||
                  formData.reason === MaintenanceReason.LINKED_TO_INCIDENT
                }
                onChange={(value) => {
                  handleInputChange('costResponsibility', value);

                  if (value === 'MITRA') {
                    handleInputChange('clientID', 0);
                    handleInputChange('clientName', '');
                    handleInputChange('oemID', '');

                    handleInputChange('driverID', 0);
                    handleInputChange('driverCode', '');
                    handleInputChange('driverName', '');
                  } else if (value === 'CLIENT') {
                    handleInputChange('oemID', '');

                    handleInputChange('driverID', 0);
                    handleInputChange('driverCode', '');
                    handleInputChange('driverName', '');
                    handleInputChange('clientID', 0);
                    handleInputChange('clientName', '');
                  } else if (value === 'OEM') {
                    handleInputChange('clientID', 0);
                    handleInputChange('clientName', '');

                    handleInputChange('driverID', 0);
                    handleInputChange('driverCode', '');
                    handleInputChange('driverName', '');
                    handleInputChange('oemID', '');
                  } else {
                    handleInputChange('clientID', 0);
                    handleInputChange('clientName', '');
                    handleInputChange('oemID', '');
                    handleInputChange('driverID', 0);
                    handleInputChange('driverCode', '');
                    handleInputChange('driverName', '');
                  }
                }}
              />
              {errors.costResponsibility && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.costResponsibility}
                </p>
              )}
            </div>

            {/* Conditional Mitra Selection */}
            {formData.costResponsibility === 'MITRA' && (
              <div className="px-6 py-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Mitra <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  placeholder="Search mitras or select..."
                  options={mitraOptions}
                  value={
                    formData.driverID > 0 ? formData.driverID.toString() : ''
                  }
                  selectedLabelFallback={selectedMitraLabel}
                  onChange={handleMitraSelection}
                  onSearchChange={setMitraSearchTerm}
                  onLoadMore={loadMoreMitras}
                  hasMore={hasMoreMitras}
                  loading={loadingMitras}
                  disableLocalFilter
                  emptyMessage="No mitras found"
                  error={!!errors.driverID}
                  disabled={isMitraSelectionDisabled}
                />
                {errors.driverID && (
                  <p className="text-red-500 text-sm mt-1">{errors.driverID}</p>
                )}
              </div>
            )}

            {/* Conditional Client Selection */}
            {formData.costResponsibility === 'CLIENT' && (
              <div className="px-6 py-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Client <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  placeholder={
                    loadingProviders ? 'Loading clients...' : 'Select client'
                  }
                  options={providers.map((provider) => ({
                    value:
                      provider.id?.toString() || provider.ID?.toString() || '',
                    label: `${provider.name}${
                      provider.email ? ` (${provider.email})` : ''
                    }`,
                  }))}
                  value={formData.clientID}
                  onChange={handleClientSelection}
                  disabled={
                    loadingProviders ||
                    incidentPrefilled ||
                    disableCostResponsibility ||
                    formData.reason === MaintenanceReason.LINKED_TO_INCIDENT
                  }
                />
                {errors.clientID && (
                  <p className="text-red-500 text-sm mt-1">{errors.clientID}</p>
                )}
                {loadingProviders && (
                  <div className="flex items-center mt-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    <span className="text-sm text-gray-500">
                      Loading clients...
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Conditional OEM Selection */}
            {formData.costResponsibility === 'OEM' && (
              <div className="px-6 py-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select OEM <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  placeholder={loadingOems ? 'Loading OEMs...' : 'Select OEM'}
                  options={oems.map((oem) => ({
                    value: oem.ID?.toString() || oem.id?.toString() || '',
                    label: `${oem.name}${
                      oem.partnershipStatus ? ` (${oem.partnershipStatus})` : ''
                    }`,
                  }))}
                  value={formData.oemID || ''}
                  onChange={handleOemSelection}
                  disabled={
                    loadingOems ||
                    incidentPrefilled ||
                    disableCostResponsibility ||
                    formData.reason === MaintenanceReason.LINKED_TO_INCIDENT
                  }
                />
                {errors.oemID && (
                  <p className="text-red-500 text-sm mt-1">{errors.oemID}</p>
                )}
                {loadingOems && (
                  <div className="flex items-center mt-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    <span className="text-sm text-gray-500">
                      Loading OEMs...
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* OEM Service Center */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                OEM Service Center <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter service center"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.oemServiceCenter}
                onChange={(e) =>
                  handleInputChange('oemServiceCenter', e.target.value)
                }
              />
              {errors.oemServiceCenter && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.oemServiceCenter}
                </p>
              )}
            </div>

            {/* Expected Completion Date */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Completion Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.expectedCompletionDate}
                onChange={(e) =>
                  handleInputChange('expectedCompletionDate', e.target.value)
                }
              />
              {errors.expectedCompletionDate && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.expectedCompletionDate}
                </p>
              )}
            </div>
          </div>
        )}

        {currentStep === FORM_STEPS.PHOTO_NOTES && (
          <div>
            {/* Detail Section Title */}
            <div>
              <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 rounded-t-lg heading-field">
                Detail
              </div>
            </div>

            {/* Image Upload */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Foto
              </label>

              <div className="border-2 border-dashed border-gray-300 rounded-lg w-full">
                {isSubmitting ? (
                  <div className="p-8 flex flex-col justify-center items-center text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-sm text-gray-600">
                      Uploading image...
                    </p>
                  </div>
                ) : !tempThumbnail && !imagePreview ? (
                  <div
                    onClick={handleImageUpload}
                    className="p-8 flex flex-col justify-center items-center text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all"
                  >
                    <div className="text-gray-400 text-4xl mb-4">
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        className="w-12 h-12 mx-auto"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <p className="font-semibold text-gray-700">
                      Add a maintenance image
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      JPEG and PNG formats, up to 10 MB.
                    </p>
                    <button
                      type="button"
                      className="mt-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Browse File
                    </button>
                  </div>
                ) : (
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="text-gray-600">
                        <svg
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          className="w-8 h-8"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {tempThumbnail?.name || 'Maintenance Image'}
                        </p>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                          {tempThumbnail?.size && (
                            <span>
                              {(tempThumbnail.size / 1024).toFixed(1)} KB
                            </span>
                          )}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-green-500"
                          >
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                          <span className="text-green-600 font-medium">
                            Completed
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="text-gray-500 hover:text-red-500 transition-colors"
                    >
                      <svg
                        className="w-5 h-5 text-red-500 hover:text-red-700 cursor-pointer"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="px-6 py-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <div className="relative">
                <textarea
                  placeholder="Enter notes..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={6}
                  maxLength={VALIDATION_RULES.NOTES_MAX_LENGTH}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                />
                <div className="absolute bottom-2 right-3 text-xs text-gray-400">
                  {formData.notes.length}/{VALIDATION_RULES.NOTES_MAX_LENGTH}
                </div>
              </div>
              {errors.notes && (
                <p className="text-red-500 text-sm mt-1">{errors.notes}</p>
              )}
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex p-6 justify-between pt-6 border-t border-gray-200">
          {currentStep === FORM_STEPS.GENERAL_DATA ? (
            <div className="flex gap-3 w-full">
              <Button
                onClick={onClose}
                variant="neutral"
                className="flex-1 py-3"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleStepNavigation(FORM_STEPS.PHOTO_NOTES)}
                variant="primary"
                className="flex-1 py-3"
                disabled={!isEdit && !isStep1Valid()}
              >
                Next Step
              </Button>
            </div>
          ) : (
            <div className="flex gap-3 w-full">
              <Button
                onClick={() => handleStepNavigation(FORM_STEPS.GENERAL_DATA)}
                variant="neutral"
                className="flex-1 py-3"
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  onFormSubmit(formData);
                }}
                variant="primary"
                disabled={isSubmitting}
                className="flex-1 py-3"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {isEdit ? 'Updating...' : 'Creating...'}
                  </div>
                ) : isEdit ? (
                  'Update Maintenance'
                ) : (
                  'Create Maintenance'
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </SideBarModal>
  );
};

export default CreateMaintenanceModal;
